import { useDeploymentStore } from '@/store/deploymentStore';
import { CognitoService } from '@/auth/CognitoService';

/**
 * Sending an email campaign to an app's mailing list.
 *
 * Only CONFIRMED subscribers are ever reached: joining a list stores a pending record and
 * sends a confirmation email, and a broadcast skips anyone who never clicked it. The
 * server adds an unsubscribe link to every message.
 *
 * `senderAddress` is a physical mailing address and is required by the server, not by
 * preference — CAN-SPAM obliges a commercial email to carry one, and a campaign tool that
 * let someone skip it would be handing them a legal problem.
 */
export interface BroadcastResult {
  /** Confirmed subscribers the send was attempted to. */
  recipients: number;
  /** How many the mail provider accepted. */
  sent: number;
}

export type CampaignFailure = 'not-signed-in' | 'no-backend' | 'not-published' | 'not-owner' | 'send-failed';

export class CampaignError extends Error {
  readonly reason: CampaignFailure;
  constructor(reason: CampaignFailure, detail?: string) {
    super(MESSAGES[reason] + (detail ? ` (${detail})` : ''));
    this.name = 'CampaignError';
    this.reason = reason;
  }
}

const MESSAGES: Record<CampaignFailure, string> = {
  'not-signed-in': 'Sign in to send a campaign.',
  'no-backend': 'No backend is configured, so there is nowhere to send from.',
  'not-published': 'Publish this app first — people can only join the list once it is live.',
  'not-owner': 'Only the owner of an app can email its list.',
  'send-failed': "The campaign didn't send.",
};

export async function sendBroadcast(
  hostedAppId: string | undefined,
  message: { subject: string; body: string; senderAddress: string },
  fetchImpl: typeof fetch = fetch,
): Promise<BroadcastResult> {
  const baseUrl = (useDeploymentStore.getState().config?.apiBaseUrl ?? '').replace(/\/+$/, '');
  if (!baseUrl) throw new CampaignError('no-backend');
  if (!hostedAppId) throw new CampaignError('not-published');
  const token = CognitoService.getAccessTokenSync();
  if (!token) throw new CampaignError('not-signed-in');

  let res: Response;
  try {
    res = await fetchImpl(`${baseUrl}/apps/${encodeURIComponent(hostedAppId)}/broadcast`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(message),
    });
  } catch {
    throw new CampaignError('send-failed');
  }
  if (res.status === 403) throw new CampaignError('not-owner');
  if (!res.ok) throw new CampaignError('send-failed', `HTTP ${res.status}`);

  const body = (await res.json()) as Partial<BroadcastResult>;
  return { recipients: body.recipients ?? 0, sent: body.sent ?? 0 };
}
